import {LensModel} from '../../../../models/api/lensModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {VisualWorkspaceModel} from '../../../../models/api/visualWorkspaceModel';
import {WorkspaceFolderRootLabelsEnum} from '../../../../models/api/workspaceModel';
import {WorkspaceDisplayActionsEnum} from '../../../../services/tracking';
import {computeReorderedItems} from '../reorder/reorderHelpers';
import {
  WorkspaceDropReorderDelegationArgs,
  WorkspaceDropReorderTagArgs,
  WorkspaceDropReorderTeamInboxArgs,
  WorkspaceDropReorderTeammateArgs,
  WorkspaceDropReorderTeammateWorkspaceItemArgs,
  WorkspaceDropReorderTeamViewArgs,
} from '../reorder/types';

type TeamDataKey = 'inboxes' | 'customViews';

type VisualWorkspaceToggleTrackingActionType =
  | WorkspaceDisplayActionsEnum.HIDE
  | WorkspaceDisplayActionsEnum.DISPLAY;

interface TrackedUpdatedVisualWorkspaceModel {
  updatedVisualWorkspace: VisualWorkspaceModel;
  trackingAction: VisualWorkspaceToggleTrackingActionType | undefined;
  isRecentlyViewed?: boolean;
}

export type WorkspaceChangeId = number;

export interface WorkspaceTeamChange {
  id: WorkspaceChangeId;
  teamId: TeamModel['id'];
}

export function toggleVisualWorkspaceRootFolder(
  visualWorkspace: VisualWorkspaceModel,
  id: WorkspaceFolderRootLabelsEnum,
): TrackedUpdatedVisualWorkspaceModel {
  let trackingAction: VisualWorkspaceToggleTrackingActionType | undefined;
  const updatedVisualWorkspace = {
    ...visualWorkspace,
    rootFolders: visualWorkspace.rootFolders.map((folder) => {
      if (folder.id !== id) {
        return folder;
      }

      trackingAction = computeVisualWorkspaceToggleTrackingAction(folder.isSelected);
      return {
        ...folder,
        isSelected: !folder.isSelected,
      };
    }),
  };
  return {
    updatedVisualWorkspace,
    trackingAction,
  };
}

/** Toggles a team inbox item in a visual workspace. This will not add an item if it is not already represented in the visual workspace team data. */
export function toggleVisualWorkspaceTeamInbox(
  visualWorkspace: VisualWorkspaceModel,
  teamChange: WorkspaceTeamChange,
): TrackedUpdatedVisualWorkspaceModel {
  return toggleVisualWorkspaceTeamItem(visualWorkspace, teamChange, 'inboxes');
}

/** Toggles a team custom view item in a visual workspace. This will not add an item if it is not already represented in the visual workspace team data. */
export function toggleVisualWorkspaceTeamCustomView(
  visualWorkspace: VisualWorkspaceModel,
  teamChange: WorkspaceTeamChange,
): TrackedUpdatedVisualWorkspaceModel {
  return toggleVisualWorkspaceTeamItem(visualWorkspace, teamChange, 'customViews');
}

/** Toggles a team item in a visual workspace. This will not add an item if it is not already represented in the visual workspace team data. */
function toggleVisualWorkspaceTeamItem(
  visualWorkspace: VisualWorkspaceModel,
  {id, teamId}: WorkspaceTeamChange,
  key: TeamDataKey,
): TrackedUpdatedVisualWorkspaceModel {
  let trackingAction: VisualWorkspaceToggleTrackingActionType | undefined;
  let isRecentlyViewed: boolean | undefined;
  const updatedVisualWorkspace = {
    ...visualWorkspace,
    teams: visualWorkspace.teams.map((teamData) => {
      if (teamData.teamId !== teamId) {
        return teamData;
      }

      return {
        ...teamData,
        [key]: teamData[key].map((item) => {
          if (item.id !== id) {
            return item;
          }

          trackingAction = computeVisualWorkspaceToggleTrackingAction(item.isSelected);
          isRecentlyViewed = item.isRecentlyViewed;
          return {
            ...item,
            isSelected: !item.isSelected,
          };
        }),
      };
    }),
  };
  return {
    updatedVisualWorkspace,
    trackingAction,
    isRecentlyViewed,
  };
}

export function showVisualWorkspaceLens(
  visualWorkspace: VisualWorkspaceModel,
  id: LensModel['id'],
): TrackedUpdatedVisualWorkspaceModel {
  const updatedVisualWorkspace = {
    ...visualWorkspace,
    lenses: [
      ...visualWorkspace.lenses.filter((visualWorkspaceLens) => visualWorkspaceLens.id !== id),
      {id, isSelected: true, isRecentlyViewed: undefined},
    ],
  };
  return {
    updatedVisualWorkspace,
    trackingAction: WorkspaceDisplayActionsEnum.DISPLAY,
    isRecentlyViewed: false,
  };
}

export function hideVisualWorkspaceLens(
  visualWorkspace: VisualWorkspaceModel,
  id: LensModel['id'],
): TrackedUpdatedVisualWorkspaceModel {
  let trackingAction: VisualWorkspaceToggleTrackingActionType | undefined;
  let isRecentlyViewed: boolean | undefined;
  const updatedVisualWorkspace = {
    ...visualWorkspace,
    lenses: visualWorkspace.lenses.map((item) => {
      if (item.id !== id || !item.isSelected) {
        return item;
      }

      trackingAction = WorkspaceDisplayActionsEnum.HIDE;
      isRecentlyViewed = item.isRecentlyViewed;
      return {
        ...item,
        isSelected: false,
      };
    }),
  };
  return {
    updatedVisualWorkspace,
    trackingAction,
    isRecentlyViewed,
  };
}

export function toggleVisualWorkspaceTeammate(
  visualWorkspace: VisualWorkspaceModel,
  id: TeammateModel['id'],
): TrackedUpdatedVisualWorkspaceModel {
  let trackingAction: VisualWorkspaceToggleTrackingActionType | undefined;
  let isRecentlyViewed: boolean | undefined;
  const updatedVisualWorkspace = {
    ...visualWorkspace,
    teammates: visualWorkspace.teammates.map((item) => {
      if (item.id !== id) {
        return item;
      }

      trackingAction = computeVisualWorkspaceToggleTrackingAction(item.isSelected);
      isRecentlyViewed = item.isRecentlyViewed;
      return {
        ...item,
        isSelected: !item.isSelected,
      };
    }),
  };
  return {
    updatedVisualWorkspace,
    trackingAction,
    isRecentlyViewed,
  };
}

export function reorderVisualWorkspaceTeammateWorkspaceTag(
  visualWorkspace: VisualWorkspaceModel,
  dropArgs: WorkspaceDropReorderTagArgs,
): VisualWorkspaceModel {
  return {
    ...visualWorkspace,
    isAlphabetized: false,
    teammateWorkspaceTags: computeReorderedItems(visualWorkspace.teammateWorkspaceTags, dropArgs),
  };
}

export function reorderVisualWorkspaceNestedFolder(
  visualWorkspace: VisualWorkspaceModel,
  {droppedFolder, targetFolder, linePosition}: WorkspaceDropReorderTeammateWorkspaceItemArgs,
): VisualWorkspaceModel {
  return {
    ...visualWorkspace,
    isAlphabetized: false,
    nestedFolders: computeReorderedItems(visualWorkspace.nestedFolders, {
      droppedItemId: droppedFolder.id,
      targetItemId: targetFolder.id,
      linePosition,
    }),
  };
}

export function reorderVisualWorkspaceTeamInbox(
  visualWorkspace: VisualWorkspaceModel,
  dropArgs: WorkspaceDropReorderTeamInboxArgs,
): VisualWorkspaceModel {
  return reorderVisualWorkspaceTeamItem(visualWorkspace, dropArgs, 'inboxes');
}

export function reorderVisualWorkspaceTeamView(
  visualWorkspace: VisualWorkspaceModel,
  dropArgs: WorkspaceDropReorderTeamViewArgs,
): VisualWorkspaceModel {
  return reorderVisualWorkspaceTeamItem(visualWorkspace, dropArgs, 'customViews');
}

function reorderVisualWorkspaceTeamItem(
  visualWorkspace: VisualWorkspaceModel,
  {teamId, ...dropArgs}: WorkspaceDropReorderTeamViewArgs | WorkspaceDropReorderTeamInboxArgs,
  key: TeamDataKey,
): VisualWorkspaceModel {
  return {
    ...visualWorkspace,
    isAlphabetized: false,
    teams: visualWorkspace.teams.map((teamWorkspace) => {
      if (teamWorkspace.teamId !== teamId) {
        return teamWorkspace;
      }
      return {
        ...teamWorkspace,
        [key]: computeReorderedItems(teamWorkspace[key], dropArgs),
      };
    }),
  };
}

export function reorderVisualWorkspaceTeammate(
  visualWorkspace: VisualWorkspaceModel,
  dropArgs: WorkspaceDropReorderTeammateArgs,
): VisualWorkspaceModel {
  return {
    ...visualWorkspace,
    isAlphabetized: false,
    teammates: computeReorderedItems(visualWorkspace.teammates, dropArgs),
  };
}

export function reorderVisualWorkspaceDelegation(
  visualWorkspace: VisualWorkspaceModel,
  dropArgs: WorkspaceDropReorderDelegationArgs,
): VisualWorkspaceModel {
  return {
    ...visualWorkspace,
    isAlphabetized: false,
    delegations: computeReorderedItems(visualWorkspace.delegations, dropArgs),
  };
}

function computeVisualWorkspaceToggleTrackingAction(
  previousSelectedValue: boolean,
): VisualWorkspaceToggleTrackingActionType {
  return previousSelectedValue ? WorkspaceDisplayActionsEnum.HIDE : WorkspaceDisplayActionsEnum.DISPLAY;
}
